import { callExternalApi } from "../../../services/external-api.service";
import { 
    IAdvisorActivity, 
    IActivityResponse, 
    IActivityRecordResponse, 
    IAdvisorShareLedgerResponse,
    AdvisorActivityInput,
    AdvisorActivityLedgerEntryStatus,
    IAdvisorActivityLedgerEntry
} from "../../../../types/database";

class AdminAdvisorShareHttpClient {
    private _apiServerUrl: string;

    constructor(apiServerUrl = process.env.REACT_APP_API_ORIGIN) {
        this._apiServerUrl = `${apiServerUrl}/v2`;
    }

    // Activity Management
    async getActivities(accessToken: string, projectId?: number): Promise<{ data: IActivityResponse | null, error: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/activities${projectId ? `?projectId=${projectId}` : ''}`,
            method: "GET",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            }
        };

        const { data, error } = await callExternalApi({ config });
        return {
            data: data?.data || null,
            error
        };
    }

    async createSystemActivity(accessToken: string, activity: AdvisorActivityInput): Promise<{ data: IAdvisorActivity | null, error: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/activities`,
            method: "POST",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            },
            data: {
                ...activity,
                is_system_wide: true
            }
        };

        const { data, error } = await callExternalApi({ config });
        return {
            data: data?.data || null,
            error
        };
    }

    async createProjectActivity(accessToken: string, projectId: number, activity: AdvisorActivityInput): Promise<{ data: IAdvisorActivity | null, error: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/projects/${projectId}/activities`,
            method: "POST",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            },
            data: {
                ...activity,
                is_system_wide: false,
                project_share_value: activity.project_share_value ?? activity.default_share_value
            }
        };

        const { data, error } = await callExternalApi({ config });
        return {
            data: data?.data || null,
            error
        };
    }

    async updateActivity(accessToken: string, activityId: number, activity: Partial<AdvisorActivityInput>): Promise<{ data: IAdvisorActivity | null, error: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/activities/${activityId}`,
            method: "PATCH",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            },
            data: activity
        };

        const { data, error } = await callExternalApi({ config });
        return {
            data: data?.data || null,
            error
        };
    }

    // Share Recording
    async recordActivityLedgerEntry(accessToken: string, payload: { key: string; advisorId: string; projectId: number; notes?: string; metadata?: Record<string, any>; status?: AdvisorActivityLedgerEntryStatus }): Promise<{ data: IAdvisorActivityLedgerEntry | null, error: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/ledger-entry`,
            method: "POST",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            },
            data: payload
        };

        const { data, error } = await callExternalApi({ config });
        return {
            data: data?.data || null,
            error
        };
    }

    // Ledger Management
    async getAdvisorLedger(accessToken: string, advisorId: string, projectId?: number): Promise<{ data: IActivityRecordResponse | null, error: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/advisor/${advisorId}/ledger${projectId ? `?projectId=${projectId}` : ''}`,
            method: "GET",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            }
        };

        const { data, error } = await callExternalApi({ config });
        return {
            data: data?.data || null,
            error
        };
    }

    async getAdvisorTotalShares(accessToken: string, advisorId: string, projectId?: number): Promise<{ data: IAdvisorShareLedgerResponse | null, error: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/advisor/${advisorId}/total${projectId ? `?projectId=${projectId}` : ''}`,
            method: "GET",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            }
        };

        const { data, error } = await callExternalApi({ config });
        return {
            data: data?.data || null,
            error
        };
    }

    // Remove an activity record from the ledger
    async deleteActivityRecord(accessToken: string, recordId: number): Promise<{ success: boolean; error?: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/ledger-entry/${recordId}`,
            method: "DELETE",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            }
        };
        const { data, error } = await callExternalApi({ config });
        if (!error) {
            return { success: true };
        } else {
            return { success: false, error };
        }
    }

    // Update the status of a ledger entry
    async updateLedgerEntryStatus(
        accessToken: string,
        ledgerEntryId: number,
        status: AdvisorActivityLedgerEntryStatus
    ): Promise<{ data: IAdvisorActivityLedgerEntry | null, error: any }> {
        const config = {
            url: `${this._apiServerUrl}/admin/advisor-activity/ledger-entry/${ledgerEntryId}`,
            method: "PATCH",
            headers: {
                "Authorization": `Bearer ${accessToken}`,
                "content-type": "application/json"
            },
            data: { status }
        };
        const { data, error } = await callExternalApi({ config });
        return {
            data: data?.data || null,
            error
        };
    }
}

export default AdminAdvisorShareHttpClient; 